import { NavLink } from "react-router-dom";
import type { SituationSummary } from "@mosaic/shared";
import {
  DISPOSITION_LABEL,
  dispositionClass,
  identityLine,
  isNear,
  whenLabel,
} from "../lib/format.js";

/**
 * One row of the radar's index column.
 *
 * Renders only what the list endpoint returns, so a row never needs a second request.
 * There is no composite score or meter: a blended number invents precision the evidence
 * does not support and becomes the thing you sort by.
 */
export function SituationRow({ situation }: { situation: SituationSummary }) {
  const s = situation;
  const days = s.daysToDistribution;
  const date = s.distributionDate ?? s.recordDate ?? s.announcedOn;

  /**
   * The dense meta line: SNDK ← WDC · 2025-02-21
   *
   * No distribution ratio: the terms grid states it with its citation and method, and a
   * bare `1:3` here would be the same fact stripped of what makes it checkable.
   */
  const meta = [identityLine(s), date].filter(Boolean).join(" · ");

  return (
    <NavLink to={`/situations/${s.id}`} className={({ isActive }) => `item${isActive ? " on" : ""}`}>
      <div className="i1">
        <b>{s.spinco.name}</b>
        <span className={`when${isNear(days) ? " near" : ""}`}>
          {whenLabel(days, s.stage, s.expectedCompletion)}
        </span>
      </div>
      <div className="i2">{meta}</div>
      {/*
        Findings do not open this strip; only the owner's own axes do.
      */}
      {(s.isPinned || s.isArchived || s.disposition !== "unreviewed") && (
        <div className="i3">
          {s.isPinned && <span className="flag good">PINNED</span>}
          {/*
            The owner's judgement, said on the row: every row in the recovery views has
            left the radar, and only this says why. A label, not a rank.
          */}
          {s.disposition !== "unreviewed" && (
            <span
              className={`flag ${dispositionClass(s.disposition)}`}
              title={s.dispositionReason ?? undefined}
            >
              {DISPOSITION_LABEL[s.disposition]}
            </span>
          )}
          {s.isArchived && <span className="flag muted">ARCHIVED</span>}
          {/*
            No flag chips: a finding is read on the situation page, beside its evidence,
            its severity and its dismiss control. The chips here are the owner's own axes
            (pinned, disposition, archived), the state of this row on the radar.
          */}
        </div>
      )}
    </NavLink>
  );
}
